import React, { useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import Button, { type ButtonProps } from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import LikeIcon from '@atlaskit/icon/core/migration/thumbs-up--like';
import { token } from '@atlaskit/tokens';
import { useFeedbackCollectorActions } from '@atlassian/jira-feedback-collector/src/controllers/index.tsx';
import type { FeedbackCollectorProps } from '@atlassian/jira-feedback-collector/src/types.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useSmartSummarizeCommentsAnalytics } from '@atlassian/jira-issue-smart-request-summary-state/src/common/analytics/index.tsx';
import {
	useProjectKey,
	useProjectType,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import { useIsProactivePanelShown } from '@atlassian/jira-issue-smart-request-summary-state/src/controllers/state/index.tsx';
import messages from './messages.tsx';

type ButtonClickHandler = NonNullable<ButtonProps['onClick']>;

const getFeedbackContext = (
	sentiment: string,
	projectType = '',
	isProactive: boolean,
): FeedbackCollectorProps => ({
	entrypointId: 'a0b04376-5026-4a3a-92b3-d2ff97ae92c3',
	feedbackCollectorId: 'ddac4251-3a49-462d-8a6b-f602fe8ac06a',
	feedbackContext: [
		{ id: 'sentiment', value: sentiment },
		{ id: 'product', value: projectType },
		{ id: 'isProactiveExperience', value: isProactive },
	],
});

export const SummaryFeedbackCollector = () => {
	const { formatMessage } = useIntl();
	const { openFeedbackCollector } = useFeedbackCollectorActions();
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const projectType = useProjectType(projectKey);
	const { fireTrack } = useSmartSummarizeCommentsAnalytics();
	const [isProactivePanelShown] = useIsProactivePanelShown();
	const [hasSubmittedFeedback, setHasSubmittedFeedback] = useState(false);

	const onClickHelpful = useCallback<ButtonClickHandler>(() => {
		fireTrack('aiFeedback submitted', 'feedbackSubmitted', {
			aiFeedbackResult: 'up',
		});
		openFeedbackCollector(getFeedbackContext('good', projectType, isProactivePanelShown));
		setHasSubmittedFeedback(true);
	}, [openFeedbackCollector, projectType, fireTrack, isProactivePanelShown]);

	const onClickUnhelpful = useCallback<ButtonClickHandler>(() => {
		fireTrack('aiFeedback submitted', 'feedbackSubmitted', {
			aiFeedbackResult: 'down',
		});

		openFeedbackCollector(getFeedbackContext('bad', projectType, isProactivePanelShown));
		setHasSubmittedFeedback(true);
	}, [openFeedbackCollector, projectType, fireTrack, isProactivePanelShown]);

	if (hasSubmittedFeedback) {
		return (
			<Container>
				<Heading size="xxsmall" as="div">
					{formatMessage(messages.thanks)}
				</Heading>
			</Container>
		);
	}

	return (
		<Container>
			<Button
				testId="issue-smart-request-summary.ui.footer.feedback.button-like"
				iconBefore={
					<LikeIcon
						LEGACY_size="small"
						label={formatMessage(messages.helpfulLabel)}
						color={token('color.text.subtlest')}
					/>
				}
				spacing="compact"
				onClick={onClickHelpful}
				appearance="subtle"
			/>
			<Button
				testId="issue-smart-request-summary.ui.footer.feedback.button-dislike"
				iconBefore={
					<DislikeIconWrapper>
						<LikeIcon
							LEGACY_size="small"
							label={formatMessage(messages.unhelpfulLabel)}
							color={token('color.text.subtlest')}
						/>
					</DislikeIconWrapper>
				}
				spacing="compact"
				onClick={onClickUnhelpful}
				appearance="subtle"
			/>
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const Container = styled.div({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const DislikeIconWrapper = styled.div({
	transform: 'rotate(180deg)',
});
